import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import moment from 'moment-timezone';
import { Observable, Subject, Subscription, timer } from 'rxjs';
import { finalize, mergeMap, takeUntil } from 'rxjs/operators';
import { WebinarDTO, WebinarType, EventInvitationStatus, UserAttenderStatus, EventType, WebinarProviderEnum } from '../../models';
import { BaseSettingsGetService, ProfileService, WebinarsService } from '../../services';
import { SETTINGS_SERVICE } from '../../constants';

@Component({
    selector: 'lib-event-registration-buttons',
    templateUrl: './event-registration-buttons.component.html',
    styleUrls: ['./event-registration-buttons.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class EventRegistrationButtonsComponent implements OnInit, OnDestroy {
  @Input() public set webinar(value: WebinarDTO) {
    this._webinar = value;
    this.init();
  }

  public get webinar() {
    return this._webinar;
  }

  @Input() public inline = false;
  @Input() public buttonCss = '';
  
  @Output() public reloadWebinar: EventEmitter<void> = new EventEmitter();
  @Output() public joined: EventEmitter<void> = new EventEmitter();
  
  public currentUserRegistered = false;

  public joinButtonAvailable = false;

  public webinarIsFull = false;

  public isLoading = false;

  public readonly types: typeof WebinarType = WebinarType;
  public readonly eventType: typeof EventType = EventType;

  public readonly invitationStatuses: typeof EventInvitationStatus = EventInvitationStatus;
  public readonly userAttenderStatuses: typeof UserAttenderStatus = UserAttenderStatus;
  public readonly webinarProviders: typeof WebinarProviderEnum = WebinarProviderEnum;

  private joinButtonAppearanceThreshold: number;
  private defaultJoinButtonThreshold = 60;
  private refreshThresholdSeconds = 3;

  private _webinar: WebinarDTO;

  private unsubscribe: Subject<void> = new Subject<void>();

  private settingsLoaded = false;
  private timerSubscription: Subscription;

  constructor(
    @Inject(SETTINGS_SERVICE) private readonly settingsService: BaseSettingsGetService,
    private readonly webinarsService: WebinarsService,
    private readonly profileService: ProfileService,
    private readonly router: Router) { }

  ngOnInit(): void {
    this.settingsService.getSettings()
      .subscribe(data => {
        this.joinButtonAppearanceThreshold = data.webinars.joinButtonAppearanceThreshold;
        this.settingsLoaded = true;
        this.init();
      });
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  public changeUserAttendance(join = true, waitingList = false): void {
    this.isLoading = true;

    this.getWebinarAttendanceObs(join, waitingList)
      .pipe(
        mergeMap(() => this.webinarsService.getWebinarById(this.webinar?.id)),
        finalize(() => this.isLoading = false)
      )
      .subscribe(data => {
        if (join) {
          this.joined.emit();
        }

        Object.assign(this.webinar, data);
        this.init();
      });
  }

  public joinWebinar(): void {
    this.webinarsService.joinWebinar(this.webinar?.id).subscribe();

    if (this.webinar.customJoinUrl) {
      window.open(this.webinar.customJoinUrl, '_blank');
      return;
    }

    if (this.webinar.provider === WebinarProviderEnum.MS_TEAMS) {
      this.joinMSTeams();
    } else {
      this.joinZoom();
    }
  }

  public joinViaApp(): void {
    window.open(this.webinar.joinUrl, '_blank');
  }

  public init(): void {
    if (!this.webinar || !this.settingsLoaded) {
      return;
    }

    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
    }

    if (this.webinar.isParent) {
      this.currentUserRegistered = false;
      this.joinButtonAvailable = false;
      return;
    }

    const { limitParticipants, attendees } = this.webinar;
  
    this.currentUserRegistered = this.webinar.invitationStatus === EventInvitationStatus.STATUS_ACCEPTED 
      || attendees?.currentUserStatus === UserAttenderStatus.USER_STATUS_CONFIRMED;

    this.webinarIsFull = limitParticipants?.enabled && attendees.max <= attendees.reserved;

    const secondsToEventStart = this.getSecondsToEventStart();
    const threshold = this.getJoinButtonThreshold(); 

    this.joinButtonAvailable = (secondsToEventStart <= threshold * 60) && this.currentUserRegistered && this.webinar.baseType === EventType.WEBINAR;
    this.setRefreshWebinarTimer();
  }

  private setRefreshWebinarTimer(): void {
    const MAX_TIMEOUT = 2147483647;

    if (this.joinButtonAvailable) {
      return;
    }

    const secondsToEventStart = this.getSecondsToEventStart();
    const threshold = this.getJoinButtonThreshold(); 

    const refreshInSeconds = (secondsToEventStart - threshold * 60) + this.refreshThresholdSeconds;

    if (refreshInSeconds <= 0 || refreshInSeconds * 1000 > MAX_TIMEOUT) {
      return;
    }

    this.timerSubscription = timer(refreshInSeconds * 1000)
      .pipe(
        takeUntil(this.unsubscribe)
      )
      .subscribe(() => {
        this.reloadWebinar.emit();
      });
  }

  private getSecondsToEventStart(): number {
    return moment(this.webinar.date).utc(false).tz(this.webinar.timezone, true).diff(moment(), 'seconds');;
  }

  private getJoinButtonThreshold(): number {
    return Number.isInteger(this.joinButtonAppearanceThreshold) && this.joinButtonAppearanceThreshold >= 0
      ? this.joinButtonAppearanceThreshold 
      : this.defaultJoinButtonThreshold; 
  }

  private joinZoom(): void {
    if (!this.webinar.isConnectedWithProvider) {
      return;
    }

    this.router.navigate(['/webinars', this.webinar.id, 'call']);
  }

  private joinMSTeams(): void {
    window.open(this.webinar.joinUrl);
  }

  private getWebinarAttendanceObs(join = false, waitingList = false): Observable<void> {
    const userId = this.profileService.getCurrentProfileValue()?.id;

    if (waitingList || this.webinar.invitationStatus === EventInvitationStatus.STATUS_NOT_INVITED) {
      return !join
        ? this.webinarsService.deleteUserFromWebinar(this.webinar.id, userId)
        : this.webinarsService.addUserToWebinar(this.webinar.id, userId);
    }

    return !join
      ? this.webinarsService.rejectInvitationForWebinar(this.webinar.id, userId)
      : this.webinarsService.acceptInvitationForWebinar(this.webinar.id, userId);
  }

}
